import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { getVouchers, createVoucher, getAccounts, generateVoucherNumber } from '@/services/firestore'
import { useAuth } from '@/contexts/AuthContext'
import { Receipt, Plus, X, Save, RefreshCw, Banknote, ArrowDownLeft, Building2, FileText } from 'lucide-react'

export const Route = createFileRoute('/admin/vouchers')({ component: VouchersPage })

const TABS = [
    { key: 'cash_payment', label: 'Cash Payment', icon: Banknote, color: 'text-red-500' },
    { key: 'cash_receipt', label: 'Cash Receipt', icon: ArrowDownLeft, color: 'text-green-500' },
    { key: 'bank_receipt', label: 'Bank Receipt', icon: Building2, color: 'text-blue-500' },
    { key: 'general_voucher', label: 'General Voucher', icon: FileText, color: 'text-purple-500' },
] as const

function VouchersPage() {
    const { user } = useAuth()
    const [activeTab, setActiveTab] = useState('cash_payment')
    const [vouchers, setVouchers] = useState<any[]>([])
    const [accounts, setAccounts] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [showModal, setShowModal] = useState(false)
    const [saving, setSaving] = useState(false)
    const [form, setForm] = useState({ debitAccountId: '', creditAccountId: '', amount: 0, narration: '', partyName: '', referenceNumber: '', date: new Date().toISOString().split('T')[0] })

    useEffect(() => { load() }, [activeTab])
    const load = async () => {
        setLoading(true)
        const [v, a] = await Promise.all([getVouchers(activeTab), getAccounts()])
        setVouchers(v); setAccounts(a)
        setLoading(false)
    }

    const handleCreate = async () => {
        setSaving(true)
        try {
            const debitAcc = accounts.find(a => a.id === form.debitAccountId)
            const creditAcc = accounts.find(a => a.id === form.creditAccountId)
            await createVoucher({
                voucherNumber: generateVoucherNumber(activeTab),
                voucherType: activeTab,
                date: form.date,
                debitAccountId: form.debitAccountId,
                debitAccountName: debitAcc?.name || '',
                creditAccountId: form.creditAccountId,
                creditAccountName: creditAcc?.name || '',
                amount: form.amount,
                narration: form.narration,
                partyName: form.partyName,
                referenceNumber: form.referenceNumber,
                createdBy: user?.email || 'admin',
            })
            await load()
            setShowModal(false)
            setForm({ debitAccountId: '', creditAccountId: '', amount: 0, narration: '', partyName: '', referenceNumber: '', date: new Date().toISOString().split('T')[0] })
        } finally { setSaving(false) }
    }

    const f = (n: number) => n.toLocaleString('en-PK', { minimumFractionDigits: 2 })

    // Auto-set debit/credit based on voucher type
    const getDefaultAccounts = () => {
        const cash = accounts.find(a => a.code === '1001')
        const bank = accounts.find(a => a.code === '1002')
        if (activeTab === 'cash_payment' && !form.creditAccountId && cash) setForm(f => ({ ...f, creditAccountId: cash.id }))
        if (activeTab === 'cash_receipt' && !form.debitAccountId && cash) setForm(f => ({ ...f, debitAccountId: cash.id }))
        if (activeTab === 'bank_receipt' && !form.debitAccountId && bank) setForm(f => ({ ...f, debitAccountId: bank.id }))
    }

    const openNew = () => {
        setForm({ debitAccountId: '', creditAccountId: '', amount: 0, narration: '', partyName: '', referenceNumber: '', date: new Date().toISOString().split('T')[0] })
        setShowModal(true)
        setTimeout(getDefaultAccounts, 100)
    }

    return (
        <div className="p-4 pt-14 lg:pt-6 lg:p-6 bg-[#f0f5ff] min-h-screen">
            <div className="flex items-center justify-between mb-5">
                <div>
                    <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                        <Receipt className="w-6 h-6 text-blue-500" />Vouchers
                    </h1>
                    <p className="text-xs text-slate-500 mt-0.5">Cash Payments, Cash Receipts, Bank Receipts & General Vouchers</p>
                </div>
                <button onClick={openNew} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors">
                    <Plus className="w-4 h-4" />New Voucher
                </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 mb-4 bg-white rounded-xl border border-slate-200/60 p-1">
                {TABS.map(tab => (
                    <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                        className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-xs font-bold transition-all ${activeTab === tab.key ? 'bg-blue-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'}`}>
                        <tab.icon className={`w-4 h-4 ${activeTab === tab.key ? 'text-white' : tab.color}`} />{tab.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="flex justify-center py-20"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : vouchers.length === 0 ? (
                <div className="text-center py-20 text-slate-400">
                    <Receipt className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm font-semibold">No {TABS.find(t => t.key === activeTab)?.label}s yet</p>
                    <p className="text-xs mt-1">Click "New Voucher" to create one</p>
                </div>
            ) : (
                <div className="bg-white rounded-xl border border-slate-200/60 shadow-sm overflow-hidden">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-100">
                                <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">Voucher #</th>
                                <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">Date</th>
                                <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">Party</th>
                                <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">Debit → Credit</th>
                                <th className="text-right px-4 py-3 text-xs font-bold text-slate-500 uppercase">Amount</th>
                                <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">Narration</th>
                            </tr>
                        </thead>
                        <tbody>
                            {vouchers.map((v, i) => (
                                <tr key={v.id} className={`border-b border-slate-50 hover:bg-slate-50/50 ${i % 2 ? 'bg-slate-25' : ''}`}>
                                    <td className="px-4 py-3 font-mono text-xs font-bold text-slate-500">{v.voucherNumber}</td>
                                    <td className="px-4 py-3 text-slate-600">{v.date}</td>
                                    <td className="px-4 py-3 font-semibold text-slate-700">{v.partyName || '—'}</td>
                                    <td className="px-4 py-3 text-xs"><span className="text-green-600 font-semibold">{v.debitAccountName}</span> <span className="text-slate-300">→</span> <span className="text-red-600 font-semibold">{v.creditAccountName}</span></td>
                                    <td className="px-4 py-3 text-right font-mono font-bold text-slate-800">{f(v.amount)}</td>
                                    <td className="px-4 py-3 text-xs text-slate-500 max-w-[200px] truncate">{v.narration}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Create Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowModal(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-bold text-slate-800">New {TABS.find(t => t.key === activeTab)?.label}</h2>
                            <button onClick={() => setShowModal(false)} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs font-bold text-slate-600 uppercase">Date</label>
                                    <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50" />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-slate-600 uppercase">Amount (PKR)</label>
                                    <input type="number" value={form.amount || ''} onChange={e => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 font-mono" />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-600 uppercase">Party Name</label>
                                <input value={form.partyName} onChange={e => setForm({ ...form, partyName: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50" placeholder="Customer or Supplier name" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs font-bold text-green-600 uppercase">Debit Account</label>
                                    <select value={form.debitAccountId} onChange={e => setForm({ ...form, debitAccountId: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50">
                                        <option value="">Select...</option>
                                        {accounts.map(a => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-red-600 uppercase">Credit Account</label>
                                    <select value={form.creditAccountId} onChange={e => setForm({ ...form, creditAccountId: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50">
                                        <option value="">Select...</option>
                                        {accounts.map(a => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-600 uppercase">Narration</label>
                                <textarea value={form.narration} onChange={e => setForm({ ...form, narration: e.target.value })} rows={2} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 resize-none" placeholder="Description of the transaction" />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-600 uppercase">Reference # (Optional)</label>
                                <input value={form.referenceNumber} onChange={e => setForm({ ...form, referenceNumber: e.target.value })} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50" placeholder="Cheque or receipt number" />
                            </div>
                            <button onClick={handleCreate} disabled={saving || !form.debitAccountId || !form.creditAccountId || !form.amount}
                                className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors">
                                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}{saving ? 'Saving...' : 'Save Voucher'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
